import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { NavigateFunction, useNavigate } from 'react-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import {
  LocationSelect,
  blankLocation,
} from '@birdi/google-places-autocomplete';
import Select from 'react-select';
import {
  stroot,
  organisation as organisationAPI,
  user as userAPI,
  onboarding as onboardingAPI,
} from '@birdi/js-sdk';
import BirdiLogoSVG from '@birdi/icons/birdi-logo-navy.svg';
import BuildingIcon from '@birdi/icons/onboarding-organisation.svg';
import BuildingIcon2 from '@birdi/icons/onboarding-organisation-2.svg';
import PhoneIcon from '@birdi/icons/onboarding-phone.svg';
import PinIcon from '@birdi/icons/onboarding-pin.svg';
import InfoSVG from '@birdi/icons/onboarding-info.svg';
import { useMetaSelector, updateMeta } from '../../../meta/reducer';
import { store } from '../../../state';
// @ts-ignore - JS file without types
import * as userActions from '../../../pilot-settings/pilot-profile/actions';
// @ts-ignore - JS file without types
import * as onboardingActions from '../../../onboarding/reducer';
import { ProgressBar } from '../progress-bar';
import * as styles from '../styles';
import { validatePhoneNumber } from '../../../onboarding/utils';
import { forms } from '@birdi/theme';

/**
 * Shared utility function to complete onboarding and redirect to dashboard
 * Used across organisation-setup, select-plan, and invite-your-team pages
 */
export const goToDashboard = async (meta: any, navigate: NavigateFunction) => {
  await onboardingAPI.completeOnboarding(stroot('quie8g'));
  // Update meta state immediately to mark onboarding as completed
  // This prevents redirect loops when navigating to dashboard
  store.dispatch(
    updateMeta({
      onboardingCompleted: true,
    }),
  );
  // Don't get caught in a loop here
  // TODO: Update meta state so that even if we're in onboarding it will kick you out
  // To avoid this complicated logic
  const redirectToOnboarding = meta.redirect?.includes('onboarding');
  let redirect = '/';
  // Testing if there's a valid redirect
  if (meta.redirect && !redirectToOnboarding) {
    redirect = meta.redirect;
  }
  navigate(redirect);
  location.reload(); // Force a reload as a hack to ensure fresh context -- introduced to reload feature flags on signup
};

const industriesList = [
  { value: 'Agriculture', label: 'Agriculture' },
  { value: 'Civil & Infrastructure', label: 'Civil & Infrastructure' },
  { value: 'Construction', label: 'Construction' },
  { value: 'Consulting', label: 'Consulting' },
  { value: 'Consumer/Business Services', label: 'Consumer/Business Services' },
  { value: 'Drone Services', label: 'Drone Services' },
  { value: 'Education', label: 'Education' },
  { value: 'Energy', label: 'Energy' },
  { value: 'Facilities Services', label: 'Facilities Services' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Forestry', label: 'Forestry' },
  { value: 'Government', label: 'Government' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Media Production', label: 'Media Production' },
  { value: 'Mining & Aggregates', label: 'Mining & Aggregates' },
  { value: 'Non Profit', label: 'Non Profit' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Research', label: 'Research' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Telecommunications', label: 'Telecommunications' },
  {
    value: 'Transportation/Trucking/Railroad',
    label: 'Transportation/Trucking/Railroad',
  },
  { value: 'Trades', label: 'Trades' },
  { value: 'Surveying', label: 'Surveying' },
  { value: 'Locating Services', label: 'Locating Services' },
  { value: 'Power', label: 'Power' },
  { value: 'Other', label: 'Other' },
];

interface OrganisationSetupProps {
  account: any;
  updateStateAndPatch: (field: any) => void;
  getProfile: () => void;
  updateAnswers: (field: any) => void;
}

const OrganisationSetup: React.FC<OrganisationSetupProps> = (props) => {
  const theme = useTheme() as BirdiTheme;
  const navigate = useNavigate();
  const { account, updateStateAndPatch, getProfile, updateAnswers } = props;
  const meta = useMetaSelector();
  const dispatch = useDispatch();
  // @ts-ignore - onboarding state type
  const onboarding = useSelector((state: any) => state.onboarding);

  // Initialize with empty values first
  const { control, formState, clearErrors, handleSubmit, reset, setValue } =
    useForm({
      defaultValues: {
        orgName: '',
        phone: '',
        location: blankLocation,
        industry: '',
      },
    });

  const [locationLoaded, setLocationLoaded] = useState(false);
  const [locationObject, setLocationObject] = useState(blankLocation);
  const [industryFetched, setIndustryFetched] = useState(false);
  const [industryFetchedSuccessfully, setIndustryFetchedSuccessfully] =
    useState(false);

  useEffect(() => {
    getProfile();
    // Always allow location to load, even if no existing location
    setLocationLoaded(true);
  }, []);

  // Fetch industry info if user is not organisation admin (org name is disabled)
  useEffect(() => {
    const isOrgAdmin = meta.permissions?.includes('organisation:edit');
    const organisationId = meta.organisation?.id;

    // If user is not org admin and we have an organisation ID, fetch industry info
    if (!isOrgAdmin && organisationId && meta.loaded && !industryFetched) {
      setIndustryFetched(true); // Mark as fetched to prevent duplicate calls
      onboardingAPI
        .getIndustryInfo(stroot('xiesh4'), organisationId)
        .then((res: any) => {
          if (res.body?.industry) {
            // Pre-fill industry field
            setValue('industry', res.body.industry);
            // Also update the onboarding state
            updateAnswers({ industry: res.body.industry });
            setIndustryFetchedSuccessfully(true); // Mark as successfully fetched
          } else {
            // API returned but no industry - allow user to select
            setIndustryFetchedSuccessfully(false);
          }
        })
        .catch((err) => {
          console.error('Error fetching industry info:', err);
          // API failed - allow user to select industry manually
          setIndustryFetchedSuccessfully(false);
        });
    }
  }, [
    meta.loaded,
    meta.permissions,
    meta.organisation?.id,
    industryFetched,
    setValue,
    updateAnswers,
  ]);

  // Reset form with existing values when meta or onboarding data is available
  // This handles:
  // 1. User coming back from next page (all fields should be pre-filled)
  // 2. Invited user - org name and industry are pre-filled and disabled, user fills the rest
  // 3. New user - all fields empty and editable
  useEffect(() => {
    if (meta.loaded || onboarding?.loaded) {
      const hasData =
        meta.organisation?.name ||
        meta.phone ||
        (meta.location && (meta.location as any).name) ||
        onboarding?.industry;
      if (hasData) {
        const newDefaults: any = {
          // Always pre-fill org name if it exists (will be disabled for non-admins)
          orgName: meta.organisation?.name || '',
          phone: meta.phone || account?.phone || '',
          location:
            meta.location && (meta.location as any).name
              ? meta.location
              : blankLocation,
          industry: onboarding?.industry || '',
        };
        reset(newDefaults);
        // Set locationObject from meta.location if available
        if (meta.location && (meta.location as any).name) {
          setLocationObject(meta.location as any);
        } else {
          // If no location in meta, reset to blankLocation
          setLocationObject(blankLocation);
        }
      }
    }
  }, [
    meta.loaded,
    meta.organisation?.name,
    meta.phone,
    meta.location,
    onboarding?.industry,
    onboarding?.loaded,
    account?.phone,
    reset,
  ]);

  const handleLocationSelect = (geocode: any) => {
    userAPI.updateMyLocation(stroot('ooleo5'), geocode.location).then((res) => {
      setLocationObject(geocode.location);
      dispatch(
        updateMeta({
          location: geocode.location,
        }),
      );
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNext = () => {
    navigate('/onboarding/select-data-region');
  };

  // Show loading only if account hasn't been fetched yet
  // Allow form to render even if account isn't fully loaded, as we can use meta state
  if (!account.loaded && !meta.loaded) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100%;
        `}
      >
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* Logo - Fixed at top */}
      <div css={styles.logoContainer}>
        <BirdiLogoSVG />
      </div>

      {/* Scrollable Content */}
      <div css={styles.scrollableContent}>
        {/* Header Section */}
        <div css={styles.headerSection}>
          <div css={styles.iconContainer}>
            <div css={styles.iconWrapper(theme)}>
              <BuildingIcon />
            </div>
            <h1 css={styles.title}>Set up your organization</h1>
          </div>

          <ProgressBar currentStep={3} totalSteps={6} />

          <p css={styles.subtitle}>Let's set up your company profile.</p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleNext)}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            @media (min-width: ${breakpoints.s}) {
              gap: 0.5rem;
            }
          `}
        >
          {/* Company Name */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="orgName">
              Company name <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <BuildingIcon2 />
              </div>
              {/* @ts-ignore - react-hook-form Controller type issue */}
              <Controller
                render={({ field }) => (
                  <input
                    id="orgName"
                    type="text"
                    css={styles.input}
                    placeholder="Enter your company name"
                    disabled={!meta.permissions?.includes('organisation:edit')}
                    value={field.value || ''}
                    onBlur={(event) => {
                      field.onBlur();
                      if (event.target.value) {
                        organisationAPI
                          .update(stroot('orgu4u'), {
                            name: event.target.value,
                          })
                          .then(() => {
                            dispatch(
                              updateMeta({
                                organisation: {
                                  ...meta.organisation,
                                  name: event.target.value,
                                },
                              }),
                            );
                          });
                      }
                    }}
                    onChange={(event) => {
                      field.onChange(event.target.value);
                      clearErrors('orgName');
                    }}
                  />
                )}
                control={control}
                name="orgName"
                rules={{ required: true }}
                defaultValue=""
              />
            </div>
            {formState.errors.orgName && (
              <div css={styles.errorMessage}>
                An organisation name is required.
              </div>
            )}
          </div>

          {/* Mobile Number */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="phone">
              Mobile number <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              <div css={styles.inputIcon}>
                <PhoneIcon />
              </div>
              <Controller
                render={({ field: { onChange, onBlur, value } }) => (
                  <input
                    id="phone"
                    type="text"
                    css={styles.input}
                    placeholder="+1 (555) 123-4567"
                    value={value || ''}
                    onChange={(event) => {
                      onChange(event.target.value);
                      clearErrors('phone');
                    }}
                    onBlur={(event) => {
                      onBlur();
                      // Save phone number only when user finishes typing (on blur)
                      updateStateAndPatch({ phone: event.target.value });
                    }}
                  />
                )}
                control={control}
                name="phone"
                rules={{
                  required: true,
                  validate: (value: string) =>
                    !value || validatePhoneNumber(value),
                }}
                defaultValue=""
              />
            </div>
            <p
              css={(theme: BirdiTheme) => css`
                font-size: 0.8125rem;
                color: ${theme.mono60};
                margin: 0.5rem 0 0 0;
              `}
            >
              Don't worry — we'll only get in touch if you've asked for help.
            </p>
            {formState.errors.phone && (
              <div css={styles.errorMessage}>
                Please provide a valid mobile number.
              </div>
            )}
          </div>

          {/* Location */}
          <div css={styles.formField}>
            <label css={styles.label} htmlFor="location">
              Location <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              {/* <div css={styles.inputIcon}>
                <PinIcon />
              </div> */}
              <Controller
                render={({ field: { onChange, value } }) => {
                  // LocationSelect expects defaultValue to be a string (location name), not the object
                  // The form state stores the location object, but we pass the name to LocationSelect
                  const locationObj =
                    value && typeof value === 'object' && (value as any).name
                      ? value
                      : locationObject &&
                          typeof locationObject === 'object' &&
                          (locationObject as any).name
                        ? locationObject
                        : null;

                  const locationName = locationObj
                    ? (locationObj as any).name
                    : '';

                  return (
                    <div
                      css={css`
                        width: 100%;
                      `}
                    >
                      <LocationSelect
                        key={`location-${locationName}-${meta.loaded}`}
                        inputId="location"
                        defaultValue={locationName}
                        onLocationSelect={(geocode) => {
                          onChange(geocode.location);
                          handleLocationSelect(geocode);
                        }}
                        placeholder="Select your location"
                        styles={forms.reactSelect}
                      />
                    </div>
                  );
                }}
                control={control}
                name="location"
                rules={{ required: true }}
                defaultValue={blankLocation}
              />
            </div>
            {formState.errors.location && (
              <div css={styles.errorMessage}>Please provide your location.</div>
            )}
          </div>

          {/* Industry */}
          <div id="industry" css={styles.formField}>
            <label css={styles.label} htmlFor="industry">
              Industry <span className="required">*</span>
            </label>
            <div css={styles.inputWrapper}>
              {/* <div css={styles.inputIcon}>
              <PinIcon />
            </div> */}
              <Controller
                render={({ field: { onChange, value } }) => {
                  const isOrgAdmin =
                    meta.permissions?.includes('organisation:edit');
                  // Only disable if user is not admin AND we successfully fetched industry from API
                  const shouldDisable =
                    !isOrgAdmin && industryFetchedSuccessfully;
                  return (
                    <div
                      css={css`
                        width: 100%;
                      `}
                    >
                      <Select
                        inputId="industry"
                        instanceId="industry"
                        options={industriesList}
                        placeholder="Select your industry"
                        value={
                          value
                            ? industriesList.find(
                                (option) => option.value === value,
                              )
                            : null
                        }
                        onChange={(option: any) => {
                          onChange(option?.value || '');
                          if (option?.value) {
                            updateAnswers({ industry: option.value });
                          }
                          clearErrors('industry');
                        }}
                        styles={forms.reactSelect}
                        isClearable
                        isDisabled={shouldDisable}
                      />
                    </div>
                  );
                }}
                control={control}
                name="industry"
                rules={{ required: true }}
                defaultValue=""
              />
            </div>
            {formState.errors.industry && (
              <div css={styles.errorMessage}>Please select your industry.</div>
            )}
          </div>

          {/* Information Box */}
          <div
            css={(theme: BirdiTheme) => css`
              background-color: ${theme.lightOcean};
              border-radius: 0.5rem;
              padding: 1rem;
              margin-top: 1rem;
              display: flex;
              gap: 0.75rem;
              align-items: flex-start;
            `}
          >
            <div
              css={(theme: BirdiTheme) => css`
                width: 1.25rem;
                height: 1.25rem;
                min-width: 1.25rem;
                color: ${theme.ocean};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1rem;
                font-weight: bold;
              `}
            >
              <InfoSVG />
            </div>
            <div
              css={(theme: BirdiTheme) => css`
                font-size: 0.875rem;
                color: ${theme.deepOcean};
                margin: 0;
                line-height: 1.5;
              `}
            >
              <strong>Why do we need this information?</strong>
              <br />
              This helps us customize your dashboard, comply with regulations,
              and provide location-specific features.
            </div>
          </div>

          {/* Buttons */}
          <div
            css={css`
              display: flex;
              gap: 0.75rem;
              margin-top: 1.5rem;
              flex-shrink: 0;
            `}
          >
            {/* <button
            type="button"
            onClick={handleBack}
            css={styles.secondaryButton(theme)}
            disabled
          >
            ← Back
          </button> */}
            <button type="submit" css={styles.primaryButton(theme)}>
              Continue →
            </button>
          </div>
        </form>
      </div>

      {/* Fixed Footer - Help Link */}
      <div css={styles.fixedFooter}>
        <div css={styles.helpLink}>
          Need help? <a href="mailto:support@birdi.io">Contact support</a>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: any) => ({
    account: state.account,
  }),
  (dispatch: any) => ({
    getProfile: () => dispatch(userActions.getProfile()),
    updateStateAndPatch: (field: any) =>
      dispatch(userActions.updateStateAndPatch(field)),
    updateAnswers: (field: any) =>
      dispatch(onboardingActions.updateAnswers(field)),
  }),
)(OrganisationSetup);
